import {
  Component,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { HomeTab } from '../../enums/home-tab';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from 'src/app/services/theme.service';
import { DynamicTitleService } from '../../services/dynamic-title.service';
import { SwitchModalComponent } from '../account/account-select/switch-modal/switch-modal.component';
import { DialogService } from '../../services/dialog.service';
import { AccountStateService } from '../../services/account-state.service';
import { Router } from '@angular/router';
import { UserService } from '../../api/services/user.service';
import { HttpHeaders } from '@angular/common/http';
import { NotificationService } from '../../api/services/notification.service';
import { StartQuestionnaireService } from '../../services/start-questionnaire.service';

@Component({
  selector: 'app-home-navbar',
  templateUrl: './home-navbar.component.html',
  standalone: false,
})
export class HomeNavbarComponent implements OnInit, OnDestroy {
  @Input() initialTab: HomeTab | undefined;
  @Output() tabChanged = new EventEmitter<HomeTab>();

  currentUserFirstName = signal<string | undefined>(undefined);
  currentAccountName = signal<string | undefined>(undefined);
  hasUnreadNotifications = signal(false);

  menuOpen = false;
  swapActive = false;
  private menuTimer?: ReturnType<typeof setTimeout>;
  private unreadNotificationCheckInFlight = false;

  selectedTab: HomeTab = HomeTab.Home;
  dynamicTitleService = inject(DynamicTitleService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private toastService: ToastService,
    private authService: AuthService,
    private themeService: ThemeService,
    private dialogService: DialogService,
    private accountStateService: AccountStateService,
    private userService: UserService,
    private notificationService: NotificationService,
    private startQuestionnaireService: StartQuestionnaireService,
  ) {
    effect((onCleanup) => {
      const accountGuid = this.authService.activeAccountGuid();
      const accountSub = accountGuid
        ? this.accountStateService
            .getAccountDetails(accountGuid)
            .subscribe((account) =>
              this.currentAccountName.set(account?.displayName),
            )
        : undefined;

      if (!accountGuid) {
        this.currentAccountName.set(undefined);
      }

      const userSub = this.userService
        .userGet(
          { userGuid: this.authService.getUserGuid() ?? undefined },
          new HttpHeaders(),
        )
        .subscribe((user) => this.currentUserFirstName.set(user?.firstName));

      onCleanup(() => {
        accountSub?.unsubscribe();
        userSub.unsubscribe();
      });
    });
  }

  ngOnInit() {
    if (this.initialTab) {
      this.selectedTab = this.initialTab;
      this.selectTab(this.selectedTab);
    }

    this.refreshUnreadNotificationState();
  }

  /**
    Select a tab, emitting an event for the parent component to consume
  */
  selectTab(tab: HomeTab) {
    this.selectedTab = tab;
    this.tabChanged.emit(tab);
  }

  /**
   * Logs the user out
   */
  logout() {
    this.authService.logout();
    void this.router.navigate(['/']);
    this.toastService.info('Logged Out');
  }

  isDarkTheme = () => this.themeService.getTheme()() === 'dark';
  canViewUsers = () => this.authService.hasAnyRole(['SuperAdmin', 'Owner']);
  canViewQuestionnaireInstances = () =>
    this.authService.hasAnyRole(['SuperAdmin', 'Owner']);

  showComingSoonToast() {
    this.toastService.info('Coming Soon!');
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) {
      this.refreshUnreadNotificationState();
    }

    if (this.menuTimer) clearTimeout(this.menuTimer);
    this.menuTimer = setTimeout(() => {
      this.swapActive = this.menuOpen;
    }, 80);
  }

  closeMenu() {
    this.menuOpen = false;
    if (this.menuTimer) clearTimeout(this.menuTimer);
    this.swapActive = false;
  }

  openStartQuestionnaireDialog(): void {
    this.closeMenu();
    this.startQuestionnaireService.openSetupDialog();
  }

  protected readonly HomeTab = HomeTab;

  onClickSwitch() {
    const dialogRef = this.dialogService.openDialog(SwitchModalComponent, {
      availableAccounts: this.accountStateService.availableAccounts.value(),
    });

    dialogRef.componentInstance.accountSwitched
      .pipe(take(1))
      .subscribe((selectedAccount) => {
        if (selectedAccount) {
          this.accountStateService.switchAccount(selectedAccount).subscribe();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.menuTimer) clearTimeout(this.menuTimer);
  }

  private refreshUnreadNotificationState(): void {
    if (this.unreadNotificationCheckInFlight) {
      return;
    }

    this.unreadNotificationCheckInFlight = true;
    this.notificationService
      .notificationsGet(
        {
          unreadOnly: true,
          pageNumber: 1,
          pageSize: 1,
        },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (notifications) => {
          this.hasUnreadNotifications.set(
            notifications.totalRecords > 0 || notifications.records.length > 0,
          );
          this.unreadNotificationCheckInFlight = false;
        },
        error: (error) => {
          console.error(error);
          this.unreadNotificationCheckInFlight = false;
        },
      });
  }
}
